// auth.js — auth-gating del frame (design §5, innesto 2): la shell non monta NULLA
// senza crm_token. Il form di login resta quello del monolite: zero modifiche al
// flusso auth. Testato con node --test (server/src/lib/shell-auth-gate.test.js, B2.14 L1).
//
// waitForAuth(): risolve quando localStorage.crm_token esiste —
//   - subito, se il token c'è già (refresh di una sessione loggata: il caso normale);
//   - altrimenti: poll 500ms + evento 'storage' (login in un altro tab) +
//     hook sul PRIMO showView post-login (il monolite chiama showView appena entra:
//     risolve subito, senza aspettare il poll).
// Dopo l'auth il gate continua a sorvegliare il token: rimosso (logout/sessione
// scaduta) → auth:changed {logged:false} → index.jsx smonta il frame.

const TOKEN_KEY = 'crm_token';

// Hook sul primo showView: wrapper PERMANENTE pass-through, inerte dopo il primo colpo.
// NON si ripristina l'originale: altri wrap (proc-v0, protocollo 2.3) possono essersi
// accodati sopra il nostro — ripristinare spezzerebbe la catena.
export function hookFirstShowView(win, onFirst) {
  const orig = win.showView;
  if (typeof orig !== 'function' || orig.__shellAuthHooked) return false;
  let fired = false;
  const wrapped = function (...args) {
    const r = orig.apply(this, args);
    if (!fired) { fired = true; try { onFirst(); } catch (e) { /* il poll resta la rete di sicurezza */ } }
    return r;
  };
  wrapped.__shellAuthHooked = true;
  win.showView = wrapped;
  return true;
}

export function createAuthGate(win, bus, opts = {}) {
  const pollMs = opts.pollMs || 500;
  const listeners = new Set();
  let logged = null;    // null = mai valutato
  let watchTimer = null;

  function getToken() {
    try { return win.localStorage.getItem(TOKEN_KEY); } catch (e) { return null; }
  }

  function setLogged(v) {
    if (logged === v) return;
    logged = v;
    bus.emit('auth:changed', { logged: v });
    listeners.forEach((fn) => { try { fn(v); } catch (e) { /* un listener rotto non ferma il gate */ } });
  }

  // Sorveglianza post-auth: token rimosso → logged:false; ricomparso → logged:true.
  function startWatch() {
    if (watchTimer) return;
    watchTimer = win.setInterval(() => { setLogged(!!getToken()); }, pollMs);
  }

  function waitForAuth() {
    if (getToken()) {
      setLogged(true);
      startWatch();
      return Promise.resolve();
    }
    setLogged(false);
    return new Promise((resolve) => {
      const t = win.setInterval(check, pollMs);
      win.addEventListener('storage', check);
      hookFirstShowView(win, check);
      function check() {
        if (!getToken()) return;
        win.clearInterval(t);
        win.removeEventListener('storage', check);
        setLogged(true);
        startWatch();
        resolve();
      }
    });
  }

  function onChange(fn) {
    listeners.add(fn);
    return () => listeners.delete(fn);
  }

  function dispose() {
    if (watchTimer) { win.clearInterval(watchTimer); watchTimer = null; }
  }

  return { waitForAuth, onChange, getToken, isLogged: () => logged === true, dispose };
}
